import { takeLatest, call, put } from 'redux-saga/effects';

import {
  checkIfAPIResponseIsSuccess,
  convertFileToBlob,
  generateUUID,
} from 'utils/functions/general';
import { getPreSignedUrl, uploadFiles } from 'modules/common/api';
import { fileUploaderIsolatedFileTypeEnum } from 'modules/common/enums';

import {
  addPost,
  getPosts,
  deletePost,
  addRssChannel,
  editRssChannel,
  getRssChannels,
} from './api';
import {
  fetchPostsListSuccess,
  fetchPostsListFail,
  fetchAddOrEditPostSubmissionSuccess,
  fetchAddOrEditPostSubmissionFail,
  fetchDeletePostSubmissionSuccess,
  fetchDeletePostSubmissionFail,
  fetchRssFeedListSuccess,
  fetchRssFeedListFail,
  fetchAddRssFeedSubmissionSuccess,
  fetchAddRssFeedSubmissionFail,
  fetchEditRssFeedSubmissionSuccess,
  fetchEditRssFeedSubmissionFail,
} from './actions';
import postsActionTypes from './action-types';

const generateMediaUploadPromise = (
  apiFileData,
  apiFilePath,
  apiFileName,
  apiRenameFile,
  isVideo,
) =>
  new Promise((resolve, reject) => {
    const preSignedUrlBody = {
      headers: {
        ServiceKey: 'doctornetwork',
        'x-vzt-Component': 'doctornetwork portal',
      },
      data: {
        FilePath: apiFilePath,
        FileName: apiFileName,
        RenameFile: apiRenameFile,
        IsPublicRead: true,
      },
    };
    getPreSignedUrl(preSignedUrlBody)
      .then(preSignedUrlResponse => {
        if (checkIfAPIResponseIsSuccess(preSignedUrlResponse.status) === false) {
          reject(new Error('Get pre-signed URL failed'));
          return;
        }

        const preSignedUrl = preSignedUrlResponse.data.data.presignedURL;
        convertFileToBlob(apiFileData).then(fileDataBlob => {
          const uploadMediaBody = {
            url: preSignedUrl,
            headers: {
              'x-amz-acl': 'public-read',
            },
            data: fileDataBlob,
          };
          uploadFiles(uploadMediaBody)
            .then(uploadMediaResponse => {
              if (checkIfAPIResponseIsSuccess(uploadMediaResponse.status) === false) {
                reject(new Error('Upload file failed'));
              }

              resolve({ url: preSignedUrlResponse.data.data.fileURL, isVideo });
            })
            .catch(() => {
              reject(new Error('Upload file failed'));
            });
        });
      })
      .catch(() => {
        reject(new Error('Get pre-signed URL failed'));
      });
  });

function* fetchPostsListSaga({ payload }) {
  const { callbackAction } = payload;

  try {
    const selectedCompanyEmail = sessionStorage.getItem('companyEmailAuthCookie');
    if (!selectedCompanyEmail) throw new Error('No company selected');

    const response = yield call(getPosts, payload);
    if (callbackAction) callbackAction();

    if (checkIfAPIResponseIsSuccess(response.status) === false) throw new Error(response.status);

    yield put(fetchPostsListSuccess({ ...response.data.data, override: payload.override }));
  } catch (error) {
    if (callbackAction) callbackAction();
    yield put(fetchPostsListFail({ error, override: payload.override }));
  }
}

function* fetchAddOrEditPostSubmissionSaga({ payload }) {
  const { callbackActionSuccess, callbackActionFail } = payload;

  try {
    const { data } = payload;
    const { postText, postTopics, postMedia, isMediaOfTypeImage } = data;

    const mediaUrlsPromises = [];
    let mediaUrls = [];

    const postKeyGuid = `post-${generateUUID()}`;

    if (postMedia.length) {
      const isVideo = postMedia[0].fileTypeId === fileUploaderIsolatedFileTypeEnum.video;

      let apiFilePath = `Post/${postKeyGuid}/images`;
      if (isVideo) apiFilePath = `Post/${postKeyGuid}/videos`;
      const thumbnailApiFilePath = `Post/${postKeyGuid}/videos`;

      postMedia.forEach(media => {
        const { id, fileName, fileData, compressedFileData } = media;
        const fileExt = fileName.split('.').pop();

        let apiFileData = compressedFileData;
        let apiFileName = `${id}.${fileExt}`;
        let apiRenameFile = false;
        if (isVideo) {
          apiFileData = fileData;
          apiFileName = `${id}.${fileExt}`;
          apiRenameFile = false;
        }

        const mediaUrlPromis = generateMediaUploadPromise(
          apiFileData,
          apiFilePath,
          apiFileName,
          apiRenameFile,
          isVideo,
        );
        mediaUrlsPromises.push(mediaUrlPromis);

        if (isVideo) {
          const thumbnailUrlPromis = generateMediaUploadPromise(
            compressedFileData,
            thumbnailApiFilePath,
            `${id}.jpg`,
            false,
            true,
          );
          mediaUrlsPromises.push(thumbnailUrlPromis);
        }
      });

      mediaUrls = yield Promise.all(mediaUrlsPromises);
    }

    const attachmentsKey = (isMediaOfTypeImage && 'Image') || 'Video';
    const attachments = mediaUrls.filter(
      (media, index) => media.isVideo === false || (media.isVideo === true && index === 0),
    );

    const companyProfileKey = sessionStorage.getItem('companyProfileKeyAuthCookie');

    const addPostBody = {
      profileKey: companyProfileKey,
      data: {
        PostKey: postKeyGuid,
        Content: postText,
        ProfileKey: companyProfileKey,
        Interests: postTopics.map(topic => topic.key),
        Attachments: attachments.map(media => ({
          Url: media.url,
          AttachmentType: attachmentsKey,
        })),
      },
    };

    const addPostResponse = yield call(addPost, addPostBody);

    if (checkIfAPIResponseIsSuccess(addPostResponse.status) === false)
      throw new Error(addPostResponse.status);

    callbackActionSuccess();
    yield put(fetchAddOrEditPostSubmissionSuccess(addPostResponse.data));
  } catch (error) {
    callbackActionFail();
    yield put(fetchAddOrEditPostSubmissionFail(error));
  }
}

function* fetchDeletePostSubmissionSaga({ payload }) {
  const { actionPopupCallbackAction } = payload;

  try {
    const response = yield call(deletePost, payload);

    if (checkIfAPIResponseIsSuccess(response.status) === false) throw new Error(response.status);

    actionPopupCallbackAction(true);
    yield put(fetchDeletePostSubmissionSuccess(response.data.data));
  } catch (error) {
    actionPopupCallbackAction();
    yield put(fetchDeletePostSubmissionFail(error));
  }
}

function* fetchRssFeedListSaga({ payload }) {
  try {
    const selectedCompanyEmail = sessionStorage.getItem('companyEmailAuthCookie');
    if (!selectedCompanyEmail) throw new Error('No company selected');

    const response = yield call(getRssChannels, payload);

    if (checkIfAPIResponseIsSuccess(response.status) === false) throw new Error(response.status);

    yield put(fetchRssFeedListSuccess(response.data));
  } catch (error) {
    yield put(fetchRssFeedListFail());
  }
}

function* fetchAddRssFeedSubmissionSaga({ payload }) {
  const { callbackActionSuccess, callbackActionFail } = payload;

  try {
    const { data } = payload;
    const { channelLink, channelTopics, channelMedia, forceOpenGraph } = data;

    const companyProfileKey = sessionStorage.getItem('companyProfileKeyAuthCookie');
    if (!companyProfileKey) throw new Error('No company selected');

    const channelKeyGuid = `chnl-${generateUUID()}`;

    const { id, fileName, compressedFileData } = channelMedia;
    const fileExt = fileName.split('.').pop();

    const apiFilePath = `Post/${channelKeyGuid}/images`;
    const apiFileData = compressedFileData;
    const apiFileName = `${id}.${fileExt}`;
    const apiRenameFile = false;

    const mediaUrlPromise = generateMediaUploadPromise(
      apiFileData,
      apiFilePath,
      apiFileName,
      apiRenameFile,
      false,
    );

    const mediaUrl = yield mediaUrlPromise;

    const addChannelBody = {
      data: {
        channelKey: channelKeyGuid,
        companyKey: companyProfileKey,
        channelLink,
        forceOpenGraphData: forceOpenGraph,
        placeholderImage: mediaUrl.url,
        tags: channelTopics.map(item => item.key),
      },
    };

    const addChannelResponse = yield call(addRssChannel, addChannelBody);

    if (checkIfAPIResponseIsSuccess(addChannelResponse.status) === false)
      throw new Error(addChannelResponse.status);

    callbackActionSuccess();
    yield put(fetchAddRssFeedSubmissionSuccess(addChannelResponse.data));
  } catch (error) {
    callbackActionFail();
    yield put(fetchAddRssFeedSubmissionFail(error));
  }
}

function* fetchEditRssFeedSubmissionSaga({ payload }) {
  const { actionPopupCallbackAction } = payload;

  try {
    const response = yield call(editRssChannel, payload);

    if (checkIfAPIResponseIsSuccess(response.status) === false) throw new Error(response.status);

    actionPopupCallbackAction(true);
    yield put(fetchEditRssFeedSubmissionSuccess(response.data.data));
  } catch (error) {
    actionPopupCallbackAction();
    yield put(fetchEditRssFeedSubmissionFail(error));
  }
}

function* postsSaga() {
  yield takeLatest(postsActionTypes.FETCH_POSTS_LIST, fetchPostsListSaga);
  yield takeLatest(
    postsActionTypes.FETCH_ADD_OR_EDIT_POST_SUBMISSION,
    fetchAddOrEditPostSubmissionSaga,
  );
  yield takeLatest(postsActionTypes.FETCH_DELETE_POST_SUBMISSION, fetchDeletePostSubmissionSaga);
  yield takeLatest(postsActionTypes.FETCH_RSS_FEED_LIST, fetchRssFeedListSaga);
  yield takeLatest(postsActionTypes.FETCH_ADD_RSS_FEED_SUBMISSION, fetchAddRssFeedSubmissionSaga);
  yield takeLatest(postsActionTypes.FETCH_EDIT_RSS_FEED_SUBMISSION, fetchEditRssFeedSubmissionSaga);
}

export default postsSaga;
